import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import {
  createRosForm_apiCalls,
  getRosFormByAppointmentId_apiCalls,
  updateRosForm_apiCalls,
} from "../../api/api_calls/appointmentforms/rosForm_apiCalls";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion-availability";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group-2";
import { useToast } from "../../hooks/use-toast";
import { fieldConfigs, rosFormSchema } from "../../schemas/rosFieldConfigs";

const ROS = ({ appointmentId, setIsEditingROS }) => {
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [existingRosForm, setExistingRosForm] = useState(null);
  const { toast } = useToast();

  // Get appointmentId from props or location state as fallback
  const finalAppointmentId =
    appointmentId ||
    location.state?.appointmentId ||
    location.state?.appointment?.id;

  // Helper function to format date for form (convert ISO to YYYY-MM-DD)
  const formatDateForForm = (isoDate) => {
    if (!isoDate) return null;
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return null;
      return date.toISOString().split("T")[0];
    } catch (error) {
      console.error("Error formatting date for form:", error);
      return null;
    }
  };

  const form = useForm({
    resolver: zodResolver(rosFormSchema),
    defaultValues: {
      appointmentId: finalAppointmentId || "",

      // GENERAL
      recentWeightChange: null,
      weakness: null,
      fatigue: null,
      fever: null,

      // SKIN
      skinRashes: null,
      skinLumps: null,
      skinSores: null,
      skinItching: null,
      skinDryness: null,
      skinChangeInColor: null,
      skinChangeInHair: null,
      skinChangeInMoles: null,

      // HEENT - Head
      headache: null,
      headInjury: null,
      dizziness: null,
      lightheadedness: null,

      // HEENT - Eyes
      visionChange: null,
      glassesOrContactLenses: null,
      eyePain: null,
      eyeRedness: null,
      excessiveTearing: null,
      doubleOrBlurredVision: null,
      spots: null,
      specks: null,
      flashingLights: null,
      glaucoma: null,
      cataracts: null,

      // HEENT - Ears
      hearingLoss: null,
      tinnitus: null,
      vertigo: null,
      earaches: null,
      earInfection: null,
      earDischarge: null,
      hearingDecreased: null,
      hearingAidsUse: null,

      // HEENT - Nose and Sinuses
      nasalDischarge: null,
      nasalItching: null,
      frequentColds: null,
      hayfever: null,
      nasalStuffiness: null,
      nosebleeds: null,
      sinusPressurePain: null,

      // HEENT - Throat
      dentalCondition: null,
      gumProblemsBleeding: null,
      dentures: null,
      denturesFit: null,
      lastDentalExam: null,
      soreTongue: null,
      dryMouth: null,
      frequentSoreThroats: null,
      hoarseness: null,

      // NECK
      swollenGlands: null,
      thyroidProblems: null,
      goiter: null,
      neckLumps: null,
      neckPainStiffness: null,

      // BREASTS
      breastLumps: null,
      breastPainDiscomfort: null,
      nippleDischarge: null,
      selfExamPractices: null,

      // RESPIRATORY
      cough: null,
      sputum: null,
      sputumColor: null,
      sputumQuantity: null,
      sputumBlood: null,
      shortnessOfBreath_respiratory: null,
      wheezing: null,
      pleuriticPain: null,
      lastChestXray: null,
      asthma: null,
      bronchitis: null,
      emphysema: null,
      pneumonia: null,
      tuberculosis: null,

      // CARDIOVASCULAR
      heartTrouble: null,
      highBloodPressure: null,
      rheumaticFever: null,
      heartMurmurs: null,
      chestPain: null,
      palpitations: null,
      shortnessOfBreath_cardio: null,
      orthopnea: null,
      paroxysmalNocturnalDyspnea: null,
      edema: null,
      ekgOther: null,

      // GASTROINTESTINAL
      unintentionalWeightChange: null,
      troubleSwallowing: null,
      heartburn: null,
      appetite: null,
      specialDiet: null,
      nausea: null,
      stoolColor: null,
      stoolSize: null,
      changeInBowelHabits: null,
      painWithDefecation: null,
      rectalBleeding: null,
      blackOrTarryStools: null,
      hemorrhoids: null,
      constipation: null,
      diarrhea: null,
      abdominalPain: null,
      foodIntolerance: null,
      excessiveBelching: null,
      jaundice: null,
      liverGallbladderTrouble: null,
      hepatitis: null,

      // PERIPHERAL VASCULAR
      claudication: null,
      legCramps: null,
      varicoseVeins: null,
      pastClots: null,
      calfLegFeetSwelling: null,
      fingertipColorChangeCold: null,
      swellingRednessTendernessPeripheral: null,

      // URINARY
      frequencyOfUrination: null,
      polyuria: null,
      nocturia: null,
      urgency: null,
      burningPainDuringUrination: null,
      hematuria: null,
      urinaryInfections: null,
      kidneyOrFlankPain: null,
      kidneyStones: null,
      ureteralColic: null,
      suprapubicPain: null,
      incontinence: null,
      reducedUrinaryCaliberOrForce: null,
      hesitancy: null,
      dribbling: null,

      // GENITAL - MALE
      hernias: null,
      penileDischarge: null,
      testicularPainOrMasses: null,
      scrotalPainOrSwelling: null,

      // STD History
      stdHistory: null,
      stdTreatment: null,

      // Sexual Habits
      sexualInterest: null,
      sexualFunction: null,
      sexualSatisfaction: null,
      birthControlMethodUse: null,
      condomUse: null,
      sexualProblems: null,
      hivInfectionConcerns: null,

      // GENITAL - FEMALE
      ageAtMenarche: null,
      periodFrequency: null,
      durationOfPeriods: null,
      amountOfBleeding: null,
      periodRegularity: null,
      bleedingBetweenPeriods: null,
      bleedingAfterIntercourse: null,
      lastMenstrualPeriod: null,
      dysmenorrhea: null,
      premenstrualTension: null,
      ageAtMenopause: null,
      menopausalSymptoms: null,
      postMenopausalBleeding: null,
      vaginalDischarge: null,
      itching_female: null,
      sores_female: null,
      lumps_female: null,
      historyOfSexuallyTransmittedInfections_female: null,
      treatmentOfSexuallyTransmittedInfections_female: null,
      numberOfPregnancies: null,
      numberOfDeliveries: null,
      typeOfDeliveries: null,
      numberOfAbortions: null,
      birthControlMethods: null,
      complicationsOfPregnancy: null,

      // SEXUAL HABITS (FEMALE-SPECIFIC)
      sexualInterest_female: null,
      sexualFunction_female: null,
      sexualSatisfaction_female: null,
      anyProblemsIncludingDyspareunia_female: null,
      concernsAboutHivInfection_female: null,

      // MUSCULOSKELETAL
      muscleOrJointPain: null,
      stiffness: null,
      arthritis: null,
      gout: null,
      backache: null,
      muscleLocationDescription: null,
      swelling_muscle: null,
      redness_muscle: null,
      painMusculoskeletal: null,
      tenderness_muscle: null,
      weaknessMusculoskeletal: null,
      numbnessInLimb: null,
      limitationOfMotionOrActivity: null,
      timingMorning: null,
      timingEvening: null,
      duration: null,
      historyOfTrauma: null,
      neckOrLowBackPain: null,
      systemicJointPain: null,

      // PSYCHIATRIC
      nervousness: null,
      tension: null,
      feelingDownSadDepressed: null,
      memoryChange: null,
      suicidePlansOrAttempts: null,
      suicidalThoughts: null,
      pastCounseling: null,
      psychiatricAdmissions: null,
      onPsychiatricMedications: null,

      // NEUROLOGIC
      changeInMoodNeurologic: null,
      changeInAttention: null,
      changeInSpeech: null,
      changeInOrientation: null,
      changeInMemoryNeurologic: null,
      changeInInsight: null,
      changeInJudgement: null,
      headacheNeurologic: null,
      dizzinessNeurologic: null,
      vertigoNeurologic: null,
      fainting: null,
      blackouts: null,
      weaknessNeurologic: null,
      paralysis: null,
      numbnessOrLossOfSensation: null,
      tinglingPinsAndNeedles: null,
      tremorsOrOtherInvoluntaryMovement: null,
      seizures: null,

      // HEMATOLOGIC
      anemia: null,
      easyBruisingOrBleeding: null,
      pastTransfusions: null,
      transfusionReactions: null,

      // ENDOCRINE
      thyroidTrouble: null,
      heatOrColdIntolerance: null,
      excessiveSweating: null,
      excessiveThirstOrHunger: null,
      polyuriaEndocrine: null,
      changeInGloveOrShoeSize: null,
    },
  });

  // Update form appointmentId when it changes (only if form is not already populated)
  useEffect(() => {
    if (finalAppointmentId && !existingRosForm) {
      form.setValue("appointmentId", finalAppointmentId);
    }
  }, [finalAppointmentId, form, existingRosForm]);

  // Fetch existing ROS form data
  useEffect(() => {
    const fetchRosForm = async () => {
      if (!finalAppointmentId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await getRosFormByAppointmentId_apiCalls({
          appointmentId: finalAppointmentId,
        });
        console.log("response :", response);
        if (response?.data) {
          setExistingRosForm(response.data);
          // Populate form with existing data - reset form with all values
          const formData = { ...response.data };
          // Remove fields that shouldn't be in the form
          delete formData.id;
          delete formData.createdAt;
          delete formData.updatedAt;
          // Ensure appointmentId is set
          formData.appointmentId = finalAppointmentId;
          // Format date fields for form compatibility
          if (formData.lastDentalExam) {
            formData.lastDentalExam = formatDateForForm(
              formData.lastDentalExam,
            );
          }
          if (formData.lastChestXray) {
            formData.lastChestXray = formatDateForForm(formData.lastChestXray);
          }
          if (formData.lastMenstrualPeriod) {
            formData.lastMenstrualPeriod = formatDateForForm(
              formData.lastMenstrualPeriod,
            );
          }
          // Reset form with all the data at once
          form.reset(formData);
        } else {
          // Reset form with default values including appointmentId
          form.reset({
            appointmentId: finalAppointmentId,
            // All other fields will use their default values from the form definition
          });
        }
      } catch (error) {
        // Don't show error toast for missing form, it's expected for new forms
      } finally {
        setLoading(false);
      }
    };
    fetchRosForm();
  }, [finalAppointmentId, form]);

  // Form submission handler
  const onSubmit = async (data) => {
    if (!finalAppointmentId) {
      toast({
        title: "Error",
        description: "No appointment ID provided",
        variant: "destructive",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      let response;
      if (existingRosForm?.id) {
        // Update existing form
        response = await updateRosForm_apiCalls({
          ...data,
          id: existingRosForm.id,
        });
      } else {
        // Create new form
        response = await createRosForm_apiCalls(data);
      }
      toast({
        title: "Success",
        description: `Review of Systems form has been ${existingRosForm?.id ? "updated" : "created"} successfully.`,
        variant: "default",
      });
      // Update existing form state if it was a create operation
      if (!existingRosForm?.id && response?.data) {
        setExistingRosForm(response.data);
      }
      if (response?.data) {
        setIsEditingROS && setIsEditingROS(); // Switch to view mode in parent
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          "There was a problem submitting the form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderRadioField = (name, label) => {
    return (
      <div className="w-full grid grid-cols-4 gap-4 items-center">
        <FormField
          control={form.control}
          name={name}
          render={({ field }) => (
            <>
              <FormItem className="col-span-2 flex items-center space-y-0">
                <FormLabel
                  id={`${name}-label`}
                  className="text-[16px] font-[600]"
                  title="Click the same option again to unselect"
                >
                  {label}
                </FormLabel>
              </FormItem>

              <FormItem className="col-span-2 flex items-center space-y-0">
                <FormControl>
                  <RadioGroup
                    onValueChange={(value) => {
                      const newValue =
                        value === "yes" ? true : value === "no" ? false : null;
                      if (
                        (value === "yes" && field.value === true) ||
                        (value === "no" && field.value === false)
                      ) {
                        field.onChange(null);
                      } else {
                        field.onChange(newValue);
                      }
                    }}
                    value={
                      field.value === true
                        ? "yes"
                        : field.value === false
                          ? "no"
                          : ""
                    }
                    className="w-full grid grid-cols-1 md:grid-cols-2 gap-2"
                    aria-labelledby={`${name}-label`}
                  >
                    <FormItem className="col-span-1 flex items-center space-x-2 space-y-0 border border-[#E7E8E9] px-8 py-4 rounded-lg hover:bg-gray-50">
                      <FormControl>
                        <RadioGroupItem
                          value="yes"
                          id={`${name}-yes`}
                          onClick={() => {
                            if (field.value === true) {
                              field.onChange(null);
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel
                        htmlFor={`${name}-yes`}
                        className="text-[16px] font-[600] leading-none cursor-pointer"
                      >
                        Yes
                      </FormLabel>
                    </FormItem>

                    <FormItem className="col-span-1 flex items-center space-x-2 space-y-0 border border-[#E7E8E9] px-4 py-2 rounded-lg hover:bg-gray-50">
                      <FormControl>
                        <RadioGroupItem
                          value="no"
                          id={`${name}-no`}
                          onClick={() => {
                            if (field.value === false) {
                              field.onChange(null);
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel
                        htmlFor={`${name}-no`}
                        className="text-[16px] font-[600] leading-none cursor-pointer"
                      >
                        No
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            </>
          )}
        />
      </div>
    );
  };
  // Function to create an input field
  const renderInputField = (name, label, placeholder = "Enter value") => {
    return (
      <div className="w-full flex flex-col gap-1">
        <FormField
          control={form.control}
          name={name}
          render={({ field }) => (
            <>
              <FormItem className="flex flex-col items-start space-y-0 w-full gap-2">
                <FormLabel
                  className="text-[16px] font-[600]"
                  style={{ color: "#1E1E1E", fontWeight: 600, fontSize: 16 }}
                >
                  {label}
                </FormLabel>
                <FormControl className="w-full">
                  <Input id={name} placeholder={placeholder} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            </>
          )}
        />
      </div>
    );
  };

  // Helper function to render radio fields from array
  const renderRadioFields = (fields) => {
    if (!fields) return null;
    return fields.map((field) => renderRadioField(field.name, field.label));
  };

  // Helper function to render input fields from array
  const renderInputFields = (fields) => {
    if (!fields) return null;
    return fields.map((field) =>
      renderInputField(field.name, field.label, field.placeholder),
    );
  };

  // Function to create a date field
  const renderDateField = (name, label, placeholder = "Select date") => {
    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <div className="w-full flex flex-col gap-2">
            <label
              className="text-[16px] font-[600] px-2"
              style={{ color: "#1E1E1E", fontWeight: 600, fontSize: 16 }}
              htmlFor={name}
            >
              {label}
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full h-[50px] justify-start text-left font-normal"
                  id={name}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {field.value
                    ? format(new Date(field.value), "MM/dd/yyyy")
                    : placeholder}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={field.value ? new Date(field.value) : undefined}
                  onSelect={(date) =>
                    field.onChange(date ? format(date, "yyyy-MM-dd") : "")
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>Loading ROS form...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="">
          <Accordion type="multiple" className="w-full space-y-4">
            {/* General */}
            <AccordionItem value="general" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  General
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.general.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Skin */}
            <AccordionItem value="skin" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">Skin</h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.skin.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* HEENT */}
            <AccordionItem value="heent-head" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">HEENT</h3>
              </AccordionTrigger>
              <AccordionContent className="">
                <div className="space-y-6 px-4">
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold my-4">Head</h2>
                    <div className="grid grid-cols-1 gap-2">
                      {renderRadioFields(fieldConfigs.heentHead.radio)}
                    </div>
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold my-4">Eyes</h2>
                    <div className="grid grid-cols-1 gap-2">
                      {renderRadioFields(fieldConfigs.heentEyes.radio)}
                    </div>
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold my-4">Ears</h2>
                    <div className="grid grid-cols-1 gap-2">
                      {renderRadioFields(fieldConfigs.heentEars.radio)}
                    </div>
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold my-4">
                      Nose and Sinuses
                    </h2>
                    <div className="grid grid-cols-1 gap-2">
                      {renderRadioFields(fieldConfigs.heentNose.radio)}
                    </div>
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold mb-4">
                      Throat (Mouth & Pharynx)
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(() => {
                        const fields = fieldConfigs.heentThroat.fields;
                        const elements = [];
                        let i = 0;
                        while (i < fields.length) {
                          const field = fields[i];
                          if (field.fieldType === "radio") {
                            elements.push(
                              <div className="col-span-2" key={field.name}>
                                {renderRadioField(field.name, field.label)}
                              </div>,
                            );
                            i++;
                          } else if (
                            field.fieldType === "input" ||
                            field.fieldType === "date"
                          ) {
                            // Check if next field is also input/date for pairing
                            const next = fields[i + 1];
                            if (
                              next &&
                              (next.fieldType === "input" ||
                                next.fieldType === "date")
                            ) {
                              elements.push(
                                <React.Fragment key={field.name + next.name}>
                                  {field.fieldType === "input"
                                    ? renderInputField(
                                        field.name,
                                        field.label,
                                        field.placeholder,
                                      )
                                    : renderDateField(
                                        field.name,
                                        field.label,
                                        field.placeholder,
                                      )}
                                  {next.fieldType === "input"
                                    ? renderInputField(
                                        next.name,
                                        next.label,
                                        next.placeholder,
                                      )
                                    : renderDateField(
                                        next.name,
                                        next.label,
                                        next.placeholder,
                                      )}
                                </React.Fragment>,
                              );
                              i += 2;
                            } else {
                              // Single input/date left, span both columns
                              elements.push(
                                <div className="col-span-2" key={field.name}>
                                  {field.fieldType === "input"
                                    ? renderInputField(
                                        field.name,
                                        field.label,
                                        field.placeholder,
                                      )
                                    : renderDateField(
                                        field.name,
                                        field.label,
                                        field.placeholder,
                                      )}
                                </div>,
                              );
                              i++;
                            }
                          } else {
                            i++;
                          }
                        }
                        return elements;
                      })()}
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Neck */}
            <AccordionItem value="neck" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">Neck</h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.neck.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Breasts */}
            <AccordionItem value="breasts" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Breasts
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.breasts.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Respiratory */}
            <AccordionItem value="respiratory" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Respiratory
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    const fields = fieldConfigs.respiratory.fields;
                    const elements = [];
                    let i = 0;
                    while (i < fields.length) {
                      const field = fields[i];
                      if (field.type === "heading") {
                        elements.push(
                          <h3
                            className="text-base font-semibold col-span-2"
                            key={field.name}
                          >
                            {field.name}
                          </h3>,
                        );
                        i++;
                      } else if (field.fieldType === "radio") {
                        elements.push(
                          <div className="col-span-2" key={field.name}>
                            {renderRadioField(field.name, field.label)}
                          </div>,
                        );
                        i++;
                      } else if (
                        field.fieldType === "input" ||
                        field.fieldType === "date"
                      ) {
                        // Check if next field is also input/date for pairing
                        const next = fields[i + 1];
                        if (
                          next &&
                          (next.fieldType === "input" ||
                            next.fieldType === "date")
                        ) {
                          elements.push(
                            <React.Fragment key={field.name + next.name}>
                              {field.fieldType === "input"
                                ? renderInputField(
                                    field.name,
                                    field.label,
                                    field.placeholder,
                                  )
                                : renderDateField(
                                    field.name,
                                    field.label,
                                    field.placeholder,
                                  )}
                              {next.fieldType === "input"
                                ? renderInputField(
                                    next.name,
                                    next.label,
                                    next.placeholder,
                                  )
                                : renderDateField(
                                    next.name,
                                    next.label,
                                    next.placeholder,
                                  )}
                            </React.Fragment>,
                          );
                          i += 2;
                        } else {
                          // Single input/date left, span both columns
                          elements.push(
                            <div className="col-span-2" key={field.name}>
                              {field.fieldType === "input"
                                ? renderInputField(
                                    field.name,
                                    field.label,
                                    field.placeholder,
                                  )
                                : renderDateField(
                                    field.name,
                                    field.label,
                                    field.placeholder,
                                  )}
                            </div>,
                          );
                          i++;
                        }
                      } else {
                        i++;
                      }
                    }
                    return elements;
                  })()}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Cardiovascular */}
            <AccordionItem value="cardiovascular" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Cardiovascular
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.cardiovascular.radio)}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInputFields(fieldConfigs.cardiovascular.input)}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Gastrointestinal */}
            <AccordionItem
              value="gastrointestinal"
              className="border rounded-lg"
            >
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Gastrointestinal
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.gastrointestinal.radio)}

                  <h3 className="text-base font-semibold">Bowel Movements</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInputFields(fieldConfigs.gastrointestinal.input)}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Peripheral Vascular */}
            <AccordionItem
              value="peripheral-vascular"
              className="border rounded-lg"
            >
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Peripheral Vascular
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.peripheralVascular.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Urinary */}
            <AccordionItem value="urinary" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Urinary
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.urinary.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Genital (Male) */}
            <AccordionItem value="genital-male" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Genital (Male)
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.genitalMale.radio)}
                  {renderRadioFields(fieldConfigs.stdHistory.radio)}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInputFields(fieldConfigs.stdHistory.input)}
                  </div>

                  <h3 className="text-base font-semibold">Sexual Habits</h3>
                  {renderRadioFields(fieldConfigs.sexualHabits.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Genital (Female) */}
            <AccordionItem value="genital-female" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Genital (Female)
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    const fields = fieldConfigs.genitalFemale.fields;
                    const elements = [];
                    let i = 0;
                    while (i < fields.length) {
                      const field = fields[i];
                      if (field.fieldType === "radio") {
                        elements.push(
                          <div className="col-span-2" key={field.name}>
                            {renderRadioField(field.name, field.label)}
                          </div>,
                        );
                        i++;
                      } else if (
                        field.fieldType === "input" ||
                        field.fieldType === "date"
                      ) {
                        // Check if next field is also input/date for pairing
                        const next = fields[i + 1];
                        if (
                          next &&
                          (next.fieldType === "input" ||
                            next.fieldType === "date")
                        ) {
                          elements.push(
                            <React.Fragment key={field.name + next.name}>
                              {field.fieldType === "input"
                                ? renderInputField(
                                    field.name,
                                    field.label,
                                    field.placeholder,
                                  )
                                : renderDateField(
                                    field.name,
                                    field.label,
                                    field.placeholder,
                                  )}
                              {next.fieldType === "input"
                                ? renderInputField(
                                    next.name,
                                    next.label,
                                    next.placeholder,
                                  )
                                : renderDateField(
                                    next.name,
                                    next.label,
                                    next.placeholder,
                                  )}
                            </React.Fragment>,
                          );
                          i += 2;
                        } else {
                          // Single input/date left, span both columns
                          elements.push(
                            <div className="col-span-2" key={field.name}>
                              {field.fieldType === "input"
                                ? renderInputField(
                                    field.name,
                                    field.label,
                                    field.placeholder,
                                  )
                                : renderDateField(
                                    field.name,
                                    field.label,
                                    field.placeholder,
                                  )}
                            </div>,
                          );
                          i++;
                        }
                      } else {
                        i++;
                      }
                    }
                    return elements;
                  })()}
                </div>
                <h3 className="text-base font-semibold mt-4">Sexual Habits</h3>
                {renderRadioFields(fieldConfigs.sexualHabitsFemale.radio)}
              </AccordionContent>
            </AccordionItem>

            {/* Musculoskeletal */}
            <AccordionItem
              value="musculoskeletal"
              className="border rounded-lg"
            >
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Musculoskeletal
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    const fields = fieldConfigs.musculoskeletal.fields;
                    const elements = [];
                    let i = 0;
                    while (i < fields.length) {
                      const field = fields[i];
                      if (field.fieldType === "radio") {
                        elements.push(
                          <div className="col-span-2" key={field.name}>
                            {renderRadioField(field.name, field.label)}
                          </div>,
                        );
                        i++;
                      } else if (field.fieldType === "input") {
                        // Check if next field is also input for pairing
                        const next = fields[i + 1];
                        if (next && next.fieldType === "input") {
                          elements.push(
                            <React.Fragment key={field.name + next.name}>
                              {renderInputField(
                                field.name,
                                field.label,
                                field.placeholder,
                              )}
                              {renderInputField(
                                next.name,
                                next.label,
                                next.placeholder,
                              )}
                            </React.Fragment>,
                          );
                          i += 2;
                        } else {
                          // Single input left, span both columns
                          elements.push(
                            <div className="col-span-2" key={field.name}>
                              {renderInputField(
                                field.name,
                                field.label,
                                field.placeholder,
                              )}
                            </div>,
                          );
                          i++;
                        }
                      } else {
                        i++;
                      }
                    }
                    return elements;
                  })()}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Psychiatric  */}
            <AccordionItem value="psychiatric" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Psychiatric
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.psychiatric.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Neurologic */}
            <AccordionItem value="neurologic" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Neurologic
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.neurologic.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Hematologic */}
            <AccordionItem value="hematologic" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Hematologic
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.hematologic.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Endocrine */}
            <AccordionItem value="endocrine" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:bg-gray-50 rounded-t-lg">
                <h3 className="text-[20px] font-[700] text-[#1E1E1E]">
                  Endocrine
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {renderRadioFields(fieldConfigs.endocrine.radio)}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="flex justify-end gap-4 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.reset();
                setIsEditingROS();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} variant="primary">
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {existingRosForm?.id ? "Updating..." : "Creating..."}
                </>
              ) : existingRosForm?.id ? (
                "Update ROS Form"
              ) : (
                "Create ROS Form"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
export default ROS;
